import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { I18n } from 'aws-amplify';
import { fetchAccountLicenses, unassignLicense } from '../../components/util/callApi';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import styles from './styles.module.css';
import formatDate from '../../components/util/formatDate';
import Dialog from '../../components/common/Dialog';
import SearchBox from '../../components/common/SearchBox';
import Button from '../../components/common/Button';
import Pagenation from '../../components/common/Pagenation';
import { PRODUCT } from '../../configures/val';

const defaultLicenses = [];
const defaultDialog = {
  open: false,
};

const perpetualPlan = 'perpetual';
/** 契約プランが買い切りの場合に有効期限欄に表示するテキスト */
const noExpireTxt = '-';

const defaultPage = 0;
const maxLicensesPerPage = 50;

const isAssignedLicense = (user) => {
  return typeof user === 'object' && Object.keys(user).length > 0;
};

/**
 * Solar Pro ライセンス管理画面
 * @param {Object} props
 * @param {Function} props.updateHeader - ヘッダの更新メソッド
 * @param {number|undefined} props.accountId - アカウント ID
 * @returns {JSX.Element}
 */
const SolarProLicenseManagement = ({ updateHeader, accountId }) => {
  const breadcrumbsListItems = [
    {
      type: 'link',
      text: I18n.get('License'),
      to: '/management/license',
    },
    {
      type: 'text',
      text: I18n.get('Details'),
    },
  ];

  const updateHeaderRef = useRef(updateHeader);
  const [conds, setConds] = useState([]);
  const [licenses, setLicenses] = useState(defaultLicenses);
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [isLoading, setIsLoading] = useState(true);
  const [dialog, setDialog] = useState(defaultDialog);

  /**
   * 検索条件に一致するライセンス一覧
   */
  const filteredLicenses = useMemo(() => {
    if (!conds.length) return licenses;

    return licenses.filter(({ user }) => {
      if (!Object.keys(user).length) return false;
      const { familyName, givenName, mailAddress } = user;
      // 名前またはメールアドレスでの絞り込み
      return conds.every(
        (cond) =>
          familyName.includes(cond) || givenName.includes(cond) || mailAddress.includes(cond)
      );
    });
  }, [licenses, conds]);

  /**
   * 現在のページで表示するライセンス一覧
   */
  const visibleLicenses = useMemo(() => {
    const start = currentPage * maxLicensesPerPage;
    return filteredLicenses.slice(start, start + maxLicensesPerPage);
  }, [currentPage, filteredLicenses]);

  /**
   * ライセンスを取得し状態変数にセットする
   */
  const loadLicenses = useCallback(async () => {
    if (typeof accountId !== 'number') return;

    try {
      let fetchAccountLicensesRes = await fetchAccountLicenses({accountId: accountId, product: PRODUCT.SOLAR_PRO});
      if (!fetchAccountLicensesRes.ok) return setLicenses(defaultLicenses);
      const { licenseCount } = fetchAccountLicensesRes.data;
      fetchAccountLicensesRes = await fetchAccountLicenses({accountId: accountId, page: 1, limit: licenseCount, product: PRODUCT.SOLAR_PRO});
      setLicenses(
        fetchAccountLicensesRes.ok ? fetchAccountLicensesRes.data.licenses : defaultLicenses
      );
    } catch (error) {
      console.error('Failed to fetch account licenses data', error);
    }

    setIsLoading(false);
  }, [accountId]);

  /**
   * 強制解除ダイアログを開く
   */
  const openForceUnassignDialog = useCallback(
    /**
     * @param {number} licenseId - 解除対象のライセンス ID
     * @param {string} userName - 解除対象のライセンスを使用中のユーザ名
     */
    (licenseId, userName) => {
      setDialog({
        open: true,
        onClose: () => setDialog(defaultDialog),
        content: (
          <div className={styles.forceUnassignDialogContent}>
            <p>
              {I18n.get('The following user is in use. Do you want to force de-authentication?')}
            </p>
            <p>{I18n.get('User with license key in use')}</p>
            <p>{userName}</p>
          </div>
        ),
        type: 'confirm',
        acceptText: I18n.get('Unassign'),
        onAccept: async () => {
          await unassignLicense(licenseId);
          setDialog(defaultDialog);
          await loadLicenses();
        },
      });
    },
    [setDialog, loadLicenses]
  );

  /**
   * 強制解除ボタンを作成する
   */
  const createForceUnassignButton = useCallback(
    /**
     * @param {number} licenseId - 解除対象のライセンス ID
     * @param {string} userName - 解除対象のライセンスを使用中のユーザ名
     * @returns {JSX.Element} 強制解除ボタン要素
     */
    (licenseId, userName) => {
      return (
        <Button
          color='rich'
          text={I18n.get('Unassign')}
          onClick={() => openForceUnassignDialog(licenseId, userName)}
          disabled={!userName}
          size='small'
        />
      );
    },
    [openForceUnassignDialog]
  );

  // ヘッダを更新する
  useEffect(() => {
    if (updateHeaderRef.current) updateHeaderRef.current();
  }, []);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  // 検索条件の更新時に先頭ページに戻す
  useEffect(() => {
    setCurrentPage(defaultPage);
  }, [conds]);

  if (isLoading) return <>{I18n.get('Now loading...')}</>;

  return (
    <div className={styles.container}>
      <Breadcrumbs listItems={breadcrumbsListItems} />
      <div className={styles.header}>
        <div className={styles.infoArea}>
          <div className={styles.productNameArea}>
            <p>{I18n.get('Product')}</p>
            <p>{`Solar Pro ${I18n.get('Network Authentication Edition')}`}</p>
          </div>
          <div className={styles.licenseInfoArea}>
            <div className={styles.licenseCountArea}>
              <p>{I18n.get('License Key')}</p>
              <p>{licenses.length}</p>
            </div>
            <div className={styles.licenseCountInUseArea}>
              <p>{I18n.get('In Use')}</p>
              <p>{licenses.filter(({ user }) => Object.keys(user).length).length}</p>
            </div>
          </div>
        </div>
        <div className={styles.searchArea}>
          <p>{`${I18n.get('Search for')}:${I18n.get('Name')}・${I18n.get('Email Address')}`}</p>
          <SearchBox search={(conds) => setConds(conds)} />
        </div>
      </div>
      <div>
        <table className={styles.licenseTable}>
          <thead>
            <tr>
              <th>{I18n.get('Product Name')}</th>
              <th>{I18n.get('Contract Plan')}</th>
              <th>{I18n.get('Effective Date')}</th>
              <th>{`${I18n.get('Name')}・${I18n.get('Email Address')}`}</th>
              <th className={styles.lastLoginDatetimeHeader}>{I18n.get('Last Login Datetime')}</th>
              <th className={styles.operationHeader}>{I18n.get('Operation')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleLicenses.map(({ id, name, plan, expire, lastLogin, user }, index) => (
              <tr key={index}>
                <td>{`Solar Pro ${I18n.get('Network Authentication Edition')}`}</td>
                <td className={styles.centerCell}>
                  {I18n.get(plan.charAt(0).toUpperCase() + plan.slice(1))}
                </td>
                <td className={styles.centerCell}>
                  {plan === perpetualPlan ? noExpireTxt : formatDate(expire, 'datetime')}
                </td>
                <td>
                  {isAssignedLicense(user) && (
                    <div className={styles.userInfo}>
                      <p>
                        {user.familyName} {user.givenName}
                      </p>
                      <p>{user.mailAddress}</p>
                    </div>
                  )}
                </td>
                <td>
                  {isAssignedLicense(user) && lastLogin ? formatDate(lastLogin, 'datetime') : ''}
                </td>
                <td className={styles.centerCell}>
                  {createForceUnassignButton(
                    id,
                    isAssignedLicense(user) ? `${user.familyName} ${user.givenName}` : null
                  )}
                </td>
              </tr>
            ))}
            {/* 検索条件に当てはまるライセンスがない場合の表示 */}
            {conds.length > 0 && visibleLicenses.length === 0 && (
              <tr>
                <td colSpan={6}>{I18n.get('No results found matching your search.')}</td>
              </tr>
            )}
          </tbody>
        </table>
        {filteredLicenses.length > maxLicensesPerPage && (
          <Pagenation
            totalPages={Math.ceil(filteredLicenses.length / maxLicensesPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
      <Dialog {...dialog} />
    </div>
  );
};

export default SolarProLicenseManagement;
